import { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { customerApi } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';

const CustomerDashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const hasFetched = useRef(false);

    useEffect(() => {
        // Prevent duplicate calls (especially in React.StrictMode)
        if (hasFetched.current) {
            return;
        }
        
        const fetchProjects = async () => {
            try {
                hasFetched.current = true;
                setLoading(true);
                setError('');
                
                // Make API call - interceptor will add Authorization header automatically
                // No need to set it explicitly, let the interceptor handle it
                const response = await customerApi.get(
                    getApiUrl('api/customer/projects')
                );
                
                // Check if response is successful and has data array
                if (response && response.data && response.data.success && response.data.data) {
                    // Get the projects array from response.data.data
                    const projectsData = response.data.data;
                    
                    // Ensure it's an array
                    if (!Array.isArray(projectsData)) {
                        console.error('Projects data is not an array:', projectsData);
                        setError('Invalid response format. Projects data is not an array.');
                        setLoading(false);
                        return;
                    }
                    
                    // Map API response to match UI structure - loop through each project
                    const mappedPlans = projectsData.map((project) => {
                        return {
                        id: project.id,
                        plan_title: project.name,
                        status: 'active', // You can add status field to API if needed
                        start_date: project.start_date,
                        end_date: project.end_date,
                        description: project.description,
                        subscription: project.subscription ? {
                            title: project.subscription.title || '',
                            short_description: project.subscription.short_description || '',
                            main_description: project.description || '',
                            start_price: project.subscription.start_price || '0',
                            end_price: project.subscription.end_price || '0',
                            price_unit: project.subscription.price_unit || 'month'
                        } : null,
                        steps: project.steps || []
                        };
                    });
                    
                    if (mappedPlans.length > 0) {
                        setPlans(mappedPlans);
                        setLoading(false);
                    } else {
                        setPlans([]);
                        setLoading(false);
                    }
                } else {
                    setError('Failed to load projects. Please try again.');
                    setLoading(false);
                }
            } catch (err) {
                console.error('Error caught in fetchProjects:', err);
                console.error('Error details:', {
                    message: err.message,
                    response: err.response,
                    request: err.request
                });
                
                console.error('Error fetching projects:', err);
                if (err.response) {
                    const { status, data } = err.response;
                    if (status === 401) {
                        setError('Session expired. Please login again.');
                    } else if (status === 404) {
                        setError('No projects found.');
                        setPlans([]); // Set empty array for 404
                    } else {
                        setError(data?.message || 'Failed to load projects. Please try again.');
                    }
                } else if (err.request) {
                    setError('Network error. Please check your connection.');
                } else {
                    setError('An error occurred. Please try again.');
                }
            } finally {
                // Always set loading to false - React will ignore if component unmounted
                setLoading(false);
            }
        };

        fetchProjects();
        
        // No cleanup needed - React will safely ignore state updates if component unmounts
    }, []);
    

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatPrice = (price) => {
        return parseFloat(price).toLocaleString('en-IN', {
            maximumFractionDigits: 0
        });
    };

    return (
        <div style={{ backgroundColor: '#f8f9fa', minHeight: 'calc(100vh - 200px)' }}>
            <Container className="py-5">
                <Row className="mb-4">
                    <Col>
                        <h2 className="mb-3" style={{ 
                            fontWeight: 'bold', 
                            fontSize: '32px',
                            color: '#677a58'
                        }}>
                            My Projects
                        </h2>
                        <p style={{ 
                            color: '#6c757d',
                            fontSize: '16px',
                            marginBottom: '0'
                        }}>
                            Click on any project to view details and track progress
                        </p>
                    </Col>
                </Row>

            {loading ? (
                <Row>
                    <Col className="text-center py-5">
                        <Spinner animation="border" style={{ color: '#677a58' }} />
                        <p style={{ 
                            marginTop: '16px',
                            color: '#6c757d',
                            fontSize: '16px'
                        }}>
                            Loading your projects...
                        </p>
                    </Col>
                </Row>
            ) : error ? (
                <Row>
                    <Col>
                        <Alert variant="danger" style={{ 
                            borderRadius: '12px',
                            border: 'none',
                            backgroundColor: '#f8d7da',
                            color: '#721c24'
                        }}>
                            <Alert.Heading style={{ fontSize: '18px', marginBottom: '8px' }}>
                                Error
                            </Alert.Heading>
                            <p style={{ marginBottom: '0', fontSize: '14px' }}>{error}</p>
                        </Alert>
                    </Col>
                </Row>
            ) : plans.length === 0 ? (
                <Row>
                    <Col>
                        <Card style={{ 
                            borderRadius: '16px', 
                            border: 'none',
                            backgroundColor: '#fff',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                        }}>
                            <Card.Body className="text-center py-5">
                                <div style={{
                                    width: '80px',
                                    height: '80px',
                                    borderRadius: '50%',
                                    backgroundColor: '#d7e4d5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 20px',
                                    color: '#677a58',
                                    fontSize: '32px'
                                }}>
                                    📋
                                </div>
                                <h5 style={{ 
                                    color: '#677a58',
                                    fontWeight: 'bold',
                                    fontSize: '20px',
                                    marginBottom: '8px'
                                }}>
                                    No Projects
                                </h5>
                                <p style={{ 
                                    color: '#6c757d',
                                    fontSize: '14px',
                                    marginBottom: '0'
                                }}>
                                    You don't have any active projects yet.
                                </p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            ) : (
                <Row className="g-4">
                    {plans.map((plan) => (
                        <Col md={6} lg={4} key={plan.id}>
                            <Card 
                                className="h-100" 
                                style={{ 
                                    borderRadius: '16px', 
                                    border: 'none', 
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    backgroundColor: '#fff',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                }}
                                onClick={() => navigate(`/customer/project-planner/${plan.id}`)}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-8px)';
                                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.12)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                                }}
                            >
                                <Card.Body className="p-4">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div style={{ flex: 1 }}>
                                            <h5 className="mb-2" style={{ 
                                                fontWeight: 'bold',
                                                fontSize: '20px',
                                                color: '#677a58',
                                                marginBottom: '8px'
                                            }}>
                                                {plan.plan_title || plan.subscription?.title || 'Project'}
                                            </h5>
                                            <p style={{ 
                                                color: '#6c757d',
                                                fontSize: '13px',
                                                marginBottom: '0'
                                            }}>
                                                Started: {formatDate(plan.start_date)}
                                            </p>
                                            {plan.end_date && (
                                                <p style={{ 
                                                    color: '#6c757d',
                                                    fontSize: '13px',
                                                    marginBottom: '0'
                                                }}>
                                                    Ends: {formatDate(plan.end_date)}
                                                </p>
                                            )}
                                        </div>
                                        <span 
                                            className="badge" 
                                            style={{ 
                                                backgroundColor: plan.status === 'active' ? '#d7e4d5' : '#e9ecef',
                                                color: plan.status === 'active' ? '#677a58' : '#6c757d',
                                                borderRadius: '20px',
                                                padding: '6px 14px',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                textTransform: 'capitalize'
                                            }}
                                        >
                                            {plan.status || 'Active'}
                                        </span>
                                    </div>
                                    
                                    {plan.description && (
                                        <p style={{ 
                                            color: '#495057',
                                            fontSize: '14px',
                                            marginBottom: '12px',
                                            lineHeight: '1.5'
                                        }}>
                                            {plan.description}
                                        </p>
                                    )}
                                    {plan.subscription && (
                                        <div style={{
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '8px',
                                            padding: '12px',
                                            marginBottom: '16px'
                                        }}>
                                            <p style={{ 
                                                marginBottom: '4px',
                                                fontSize: '14px',
                                                color: '#495057'
                                            }}>
                                                <strong style={{ color: '#677a58' }}>Plan:</strong> {plan.subscription.title || 'N/A'}
                                            </p>
                                            {(plan.subscription.start_price || plan.subscription.end_price) && (
                                                <p style={{ 
                                                    marginBottom: '0',
                                                    fontSize: '14px',
                                                    color: '#495057'
                                                }}>
                                                    <strong style={{ color: '#677a58' }}>Price:</strong> ₹{formatPrice(plan.subscription.start_price || '0')}
                                                    {plan.subscription.end_price && plan.subscription.end_price !== '0' && ` - ₹${formatPrice(plan.subscription.end_price)}`}
                                                    <span style={{ color: '#6c757d', fontSize: '12px' }}> /{plan.subscription.price_unit || 'month'}</span>
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    <div className="mt-3">
                                        <button
                                            className="w-100"
                                            style={{
                                                backgroundColor: '#677a58',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '8px',
                                                padding: '12px 24px',
                                                fontWeight: '600',
                                                fontSize: '14px',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#5a6b4d';
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = '#677a58';
                                                e.currentTarget.style.transform = 'translateY(0)';
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/customer/project-planner/${plan.id}`);
                                            }}
                                        >
                                            View Details
                                        </button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
            </Container>
        </div>
    );
};

export default CustomerDashboard;

